<script lang="ts" setup>
import { ElMessage } from "element-plus";
import type { CheckboxValueType } from "element-plus";
import { QuestionFilled } from "@element-plus/icons-vue";
import {
  formatNumberWithSpaces,
  fiatConvert,
  cryptoConvert,
  numberExponentToLarge,
} from "@/shared/utils";
import { createOffer, editOffer, getCryptoBalanceAddress } from "@/api";
import { OfferPriceType, OfferType } from "@/shared/enums";
import type { OfferForm } from "@/shared/types";
import { MIN_OFFER_AMOUNT_USD } from "@/shared/consts";
const RatesStore = useRatesStore();
const { rates } = storeToRefs(RatesStore);
const CurrencyStore = useCurrencyStore();
const { cryptoCurrencies, fiatCurrencies } = storeToRefs(CurrencyStore);
const { getSympolById } = CurrencyStore;
const PaymentOptionsStore = usePaymentOptionsStore();
const { getMyOptions, getOptionById } = PaymentOptionsStore;
const { myOptions } = storeToRefs(PaymentOptionsStore);
const { width } = useWindowSize();
const { t } = useI18n();
const emit = defineEmits<{
  (e: "close", opts?: any): void;
  (e: "addOption", currency?: string): void;
}>();

const props = defineProps<{
  form?: OfferForm;
  preCreatedOption?: number;
}>();

const show = ref(true);
const onClose = () => {
  ElMessageBox.confirm(t("c-offers-editform-closeConfirmation"))
    .then(() => {
      emit("close");
    })
    .catch(() => {});
  return false;
};
const priceDivider = 100;

const form = reactive({
  offerId: props.form?.offerId || null,
  type: props.form?.type || "",
  currencyIn: props.form?.currencyIn || "",
  currencyOut: props.form?.currencyOut || "",
  priceType: props.form?.priceType || "",
  priceVal:
    (props.form?.priceVal ? +props.form?.priceVal / priceDivider : "") || 0,
  userPaymentOptions: props.form?.userPaymentOptions || [],
  minLimit: props.form?.minLimit || "",
  maxLimit: props.form?.maxLimit || "",
  termsOfUse: props.form?.termsOfUse || "",
  isUnlimited: props.form?.isUnlimited || false,
  offerAmount: props.form?.offerAmount || "",
});

const helpPriceTypesVisible = ref(false);
const helpLimitsVisible = ref(false);
const helpOfferAmountVisible = ref(false);

onBeforeMount(() => {
  getMyOptions();
});

const fullScreen = computed(() => width.value < 620);
const customRate = computed(() => {
  let num = 0;
  const currRate = rates.value[form.currencyIn]?.[form.currencyOut];
  if (form.priceType === OfferPriceType.Percents) {
    num = currRate * (1 + Number(form.priceVal) / 100);
  } else if (form.priceType === OfferPriceType.Absolute) {
    num = currRate + Number(form.priceVal);
  } else if (form.priceType === OfferPriceType.Custom) {
    num = Number(form.priceVal);
  }
  return formatNumberWithSpaces(num.toFixed(2));
});

const loading = ref(false);
const textError = ref<null | string>(null);
const editForm = ref<null | any>(null);

const save = async () => {
  editForm.value.validate(async (valid: boolean) => {
    if (!valid) return;
    await saveOffer();
  });
};

const saveOffer = async () => {
  loading.value = true;
  textError.value = null;
  const modelForm = {
    ...form,
  };
  modelForm.minLimit = fiatConvert(Number(modelForm.minLimit)).toString();
  modelForm.maxLimit = fiatConvert(Number(modelForm.maxLimit)).toString();
  modelForm.offerAmount = cryptoConvert(
    Number(modelForm.offerAmount),
    form.currencyIn
  ).toString();
  modelForm.priceVal = Math.floor(+modelForm.priceVal * priceDivider);
  try {
    if (!form.offerId) {
      await createOffer({
        ...modelForm,
        isActive: false,
      });
    } else {
      await editOffer({
        ...modelForm,
        isActive: props.form?.isActive || false,
      });
    }
  } catch (e: any) {
    let msg = e?.data?.data?.message || "";
    const code = e?.data?.data?.code;
    switch (code) {
      case 1206:
        msg = t("c-offers-editform-percentError");
        break;
      default:
        if (msg === "") msg = t("c-offers-editform-createError");
        break;
    }
    ElMessage.error(msg);
    textError.value = msg;
    loading.value = false;
    return;
  }
  if (!form.offerId) {
    ElMessage.success(t("c-offers-editform-createSuccess"));
  } else {
    ElMessage.success(t("c-offers-editform-updateSuccess"));
  }
  emit("close", { fetch: true });
};

const editing = computed(() => !!form.offerId);
const rules = computed(() => ({
  type: [
    {
      required: true,
      message: t("c-offers-editform-typeRequired"),
      trigger: "change",
    },
  ],
  currencyIn: [
    {
      required: true,
      message: t("c-offers-editform-currencyRequired"),
      trigger: "change",
    },
  ],
  currencyOut: [
    {
      required: true,
      message: t("c-offers-editform-currencyRequired"),
      trigger: "change",
    },
    {
      validator: (_: any, _v: string, callback: any) => {
        if (form.minLimit !== "") {
          editForm.value.validateField("minLimit");
        }
        callback();
      },
      trigger: "change",
    },
  ],
  priceType: [
    {
      required: true,
      message: t("c-offers-editform-priceTypeRequired"),
      trigger: "change",
    },
  ],
  priceVal: [
    {
      required: true,
      message: t("c-offers-editform-priceValRequired"),
      trigger: "change",
    },
  ],
  userPaymentOptions: [
    {
      required: true,
      message: t("c-offers-editform-paymentOptionsRequired"),
      trigger: "change",
    },
  ],
  minLimit: [
    {
      required: true,
      message: t("c-offers-editform-minLimitRequired"),
      trigger: "change",
    },
    {
      validator: (_: any, value: string, callback: any) => {
        if (form.maxLimit !== "") {
          editForm.value.validateField("maxLimit");
        }
        if (
          +value <=
          +rates.value.USDT[form.currencyOut] * MIN_OFFER_AMOUNT_USD
        ) {
          callback(
            new Error(
              t("c-offers-editform-minLimitError", {
                minAmount:
                  rates.value.USDT[form.currencyOut] * MIN_OFFER_AMOUNT_USD,
              })
            )
          );
        } else {
          callback();
        }
      },
      trigger: "change",
    },
  ],
  maxLimit: [
    {
      required: true,
      message: t("c-offers-editform-maxLimitRequired"),
      trigger: "change",
    },
    {
      validator: (_: any, value: string, callback: any) => {
        if (+value < +form.minLimit) {
          callback(new Error(t("c-offers-editform-maxLimitError")));
        } else {
          callback();
        }
      },
      trigger: "change",
    },
  ],
  offerAmount: [
    {
      validator: (_: any, value: string, callback: any) => {
        if (!form.isUnlimited && !value) {
          callback(new Error(t("c-offers-editform-offerAmountRequired")));
        } else {
          callback();
        }
      },
      trigger: "change",
    },
  ],
  termsOfUse: [
    {
      required: true,
      message: t("c-offers-editform-termsOfUseRequired"),
      trigger: "change",
    },
  ],
}));
const myOptionsFilterd = computed(() => {
  return form.currencyOut
    ? myOptions.value.filter((option: any) => {
        return option.currency === form.currencyOut;
      })
    : myOptions.value;
});
watch(
  () => form.currencyOut,
  () => {
    form.userPaymentOptions = form.userPaymentOptions.filter((id) => {
      return myOptionsFilterd.value.some((option: any) => option.upo_id === id);
    });
  }
);
const onAddOption = () => {
  emit("addOption", form.currencyOut);
};
const loadingBalance = ref(false);
const currentBalanceCoin = ref("");

watch(
  () => form.currencyIn,
  async () => {
    if (form.currencyIn) {
      loadingBalance.value = true;
      const balanceData = await getCryptoBalanceAddress(form.currencyIn);
      currentBalanceCoin.value = formatNumberWithSpaces(
        numberExponentToLarge(
          cryptoConvert(
            balanceData.balance - balanceData.frozenBalance,
            form.currencyIn,
            true
          ),
          10
        )
      );
      loadingBalance.value = false;
    }
  },
  { immediate: true }
);
const checkAll = ref(false);
const indeterminate = ref(false);
watch(
  () => form.userPaymentOptions,
  (val) => {
    if (val.length === 0) {
      checkAll.value = false;
      indeterminate.value = false;
    } else if (val.length === myOptionsFilterd.value.length) {
      checkAll.value = true;
      indeterminate.value = false;
    } else {
      indeterminate.value = true;
    }
  }
);

const handleCheckAll = (val: CheckboxValueType) => {
  indeterminate.value = false;
  if (val) {
    form.userPaymentOptions = myOptionsFilterd.value.map(
      (option) => option.upo_id
    );
  } else {
    form.userPaymentOptions = [];
  }
};
watch(
  () => props.preCreatedOption,
  (val) => {
    if (val) {
      form.userPaymentOptions.push(val);
    }
  }
);
</script>

<template>
  <el-dialog
    :title="
      editing
        ? t('c-offers-editform-editTitle')
        : t('c-offers-editform-addTitle')
    "
    width="600px"
    :fullscreen="fullScreen"
    v-model="show"
    :before-close="onClose"
    :destroy-on-close="true"
  >
    <el-form
      class="offer-form"
      v-loading="loading"
      :model="form"
      :rules="rules"
      :label-width="fullScreen ? 'auto' : '250px'"
      :label-position="fullScreen ? 'top' : 'right'"
      style="max-width: 600px"
      ref="editForm"
    >
      <el-form-item :label="t('c-offers-editform-offerType')" prop="type">
        <el-radio-group
          v-model="form.type"
          :disabled="editing"
          @change="form.isUnlimited = false"
        >
          <el-radio-button
            :label="t('c-offers-editform-sell')"
            class="order-type-sell"
            value="sell"
          />
          <el-radio-button
            :label="t('c-offers-editform-buy')"
            class="order-type-buy"
            value="buy"
          />
        </el-radio-group>
      </el-form-item>
      <el-form-item
        prop="currencyIn"
        :label="`${
          form.type === 'sell'
            ? t('c-offers-editform-selling')
            : form.type === 'buy'
            ? t('c-offers-editform-buying')
            : t('c-offers-editform-mainCurrency')
        }`"
      >
        <el-select
          v-model="form.currencyIn"
          :placeholder="t('c-offers-editform-selectCurrency')"
          :disabled="editing"
        >
          <el-option
            v-for="curr in cryptoCurrencies"
            :key="curr.name"
            :label="curr.name"
            :value="curr.name"
          />
        </el-select>
      </el-form-item>
      <el-form-item
        :label="t('c-offers-editform-forCurrency')"
        prop="currencyOut"
      >
        <el-select
          v-model="form.currencyOut"
          :placeholder="t('c-offers-editform-selectCurrency')"
          :disabled="editing"
        >
          <el-option
            v-for="curr in fiatCurrencies"
            :key="curr.name"
            :label="curr.name"
            :value="curr.name"
          />
        </el-select>
      </el-form-item>
      <Transition>
        <div
          class="font-size-3.5 text-right"
          v-if="form.currencyIn && form.currencyOut"
        >
          {{
            t("c-offers-editform-currentRate", {
              currencyIn: form.currencyIn,
              rate: formatNumberWithSpaces(
                rates?.[form.currencyIn]?.[form.currencyOut] || 0
              ),
              currencyOut: form.currencyOut,
            })
          }}
        </div>
      </Transition>
      <el-divider>{{ t("c-offers-editform-price") }}</el-divider>
      <el-form-item :label="t('c-offers-editform-priceType')" prop="priceType">
        <el-row :gutter="10" class="w-full">
          <el-col :span="-1" class="flex-grow">
            <el-select
              v-model="form.priceType"
              :placeholder="t('c-offers-editform-selectPriceType')"
            >
              <el-option
                :label="t('c-offers-editform-percentValue')"
                :value="OfferPriceType.Percents"
              />
              <el-option
                :label="t('c-offers-editform-relativeValue')"
                :value="OfferPriceType.Absolute"
              />
              <el-option
                :label="t('c-offers-editform-fixedValue')"
                :value="OfferPriceType.Custom"
              />
            </el-select>
          </el-col>
          <el-col :span="-1">
            <el-button circle text @click="helpPriceTypesVisible = true">
              <el-icon><QuestionFilled /></el-icon>
            </el-button>
            <el-dialog
              v-model="helpPriceTypesVisible"
              :title="t('c-offers-editform-priceTypesExplained')"
              width="500px"
              :fullscreen="fullScreen"
            >
              <div class="font-size-3.5">
                <p>
                  <b class="color-primary"
                    >{{ t("c-offers-editform-percent") }}:</b
                  >
                  {{ t("c-offers-editform-percentExplanation") }}
                </p>
                <p>
                  <b class="color-primary"
                    >{{ t("c-offers-editform-relative") }}:</b
                  >
                  {{ t("c-offers-editform-relativeExplanation") }}
                </p>
                <p>
                  <b class="color-primary"
                    >{{ t("c-offers-editform-fixed") }}:</b
                  >
                  {{ t("c-offers-editform-fixedExplanation") }}
                </p>
              </div>
            </el-dialog>
          </el-col>
        </el-row>
      </el-form-item>
      <el-form-item
        prop="priceVal"
        :label="`${
          form.priceType === OfferPriceType.Percents
            ? t('c-offers-editform-pricePercent')
            : ''
        }${
          form.priceType === OfferPriceType.Absolute
            ? t('c-offers-editform-priceRelative')
            : ''
        }${
          form.priceType === OfferPriceType.Custom
            ? t('c-offers-editform-priceFixed')
            : ''
        }`"
      >
        <el-input
          type="number"
          style="width: 300px"
          v-model="form.priceVal"
          :controls="false"
          :placeholder="t('c-offers-editform-enterPriceValue')"
        >
          <template #suffix v-if="form.priceType === OfferPriceType.Percents"
            >%</template
          >
        </el-input>
      </el-form-item>
      <Transition>
        <div
          class="font-size-3.5 text-right"
          v-if="form.currencyIn && form.currencyOut && form.priceType"
        >
          {{
            t("c-offers-editform-rate", {
              currencyIn: form.currencyIn,
              rate: customRate,
              currencyOut: form.currencyOut,
            })
          }}
        </div>
      </Transition>
      <el-divider>{{ t("c-offers-editform-paymentOptions") }}</el-divider>
      <el-form-item
        :label="t('c-offers-editform-paymentOptions')"
        prop="userPaymentOptions"
      >
        <el-select
          v-model="form.userPaymentOptions"
          multiple
          :placeholder="t('c-offers-editform-selectPaymentOptions')"
        >
          <template #empty>
            <p>
              {{ t("c-offers-editform-noOptions") }}
              <span v-if="form.currencyOut">{{
                t("c-offers-editform-forCurrency", {
                  currencyOut: form.currencyOut,
                })
              }}</span>
            </p>
          </template>
          <template #header v-if="myOptionsFilterd.length > 1">
            <el-checkbox
              v-model="checkAll"
              :indeterminate="indeterminate"
              @change="handleCheckAll"
            >
              {{ t("c-offers-editform-all") }}
            </el-checkbox>
          </template>
          <el-option
            v-for="option in myOptionsFilterd"
            :key="option.upo_id"
            :label="`${getOptionById(option.base_id)?.name} - ${
              option.currency
            }`"
            :value="option.upo_id"
          />
          <template #footer>
            <el-button size="small" class="mr-1" @click="onAddOption()" round>
              {{ t("c-offers-editform-addOption") }}
            </el-button>
          </template>
        </el-select>
      </el-form-item>
      <el-form-item :label="t('c-offers-editform-minLimit')" prop="minLimit">
        <el-input
          type="number"
          style="width: 300px"
          v-model="form.minLimit"
          :controls="false"
          :placeholder="t('c-offers-editform-enterMinAmount')"
        >
          <template #suffix>{{ getSympolById(form.currencyOut) }}</template>
        </el-input>
      </el-form-item>
      <el-form-item :label="t('c-offers-editform-maxLimit')" prop="maxLimit">
        <el-input
          type="number"
          style="width: 300px"
          v-model="form.maxLimit"
          :controls="false"
          :placeholder="t('c-offers-editform-enterMaxAmount')"
        >
          <template #suffix>{{ getSympolById(form.currencyOut) }}</template>
        </el-input>
      </el-form-item>
      <el-form-item label=" ">
        <el-button
          round
          size="small"
          class="text-decoration-dotted"
          text
          @click="helpLimitsVisible = true"
        >
          {{ t("c-offers-editform-orderLimitsExplanation") }}
          <el-icon class="ml-1"><QuestionFilled /></el-icon>
        </el-button>
        <el-dialog
          v-model="helpLimitsVisible"
          :title="t('c-offers-editform-orderLimitsExplanationTitle')"
          width="500px"
          :fullscreen="fullScreen"
        >
          <div class="font-size-3.5">
            <p>
              {{ t("c-offers-editform-orderLimitsExplanationText") }}
            </p>
            <p>
              {{
                t("c-offers-editform-orderLimitsExplanationUSD", {
                  usdLimit: rates.USDT[form.currencyOut] * MIN_OFFER_AMOUNT_USD,
                  currencyOut: getSympolById(form.currencyOut),
                  MIN_OFFER_AMOUNT_USD,
                })
              }}
            </p>
          </div>
        </el-dialog>
      </el-form-item>
      <el-divider>{{ t("c-offers-editform-amountToTrade") }}</el-divider>
      <el-form-item
        :label="t('c-offers-editform-offerAmount')"
        prop="offerAmount"
      >
        <div v-if="form.type === OfferType.Sell.toLowerCase()">
          <el-checkbox v-model="form.isUnlimited">{{
            t("c-offers-editform-offerAmount-unlimited")
          }}</el-checkbox>
        </div>
        <Transition>
          <el-input
            type="number"
            style="width: 300px"
            v-model="form.offerAmount"
            v-if="
              !form.isUnlimited || form.type === OfferType.Buy.toLowerCase()
            "
            :controls="false"
            :placeholder="`${
              form.type === 'sell'
                ? t('c-offers-editform-selling')
                : form.type === 'buy'
                ? t('c-offers-editform-buying')
                : ''
            } ${t('c-offers-editform-enterOfferAmount')}`"
          >
            <template #suffix>{{ getSympolById(form.currencyIn) }}</template>
          </el-input>
        </Transition>
        <template v-if="form.type === OfferType.Sell.toLowerCase()">
          <el-skeleton :rows="1" animated v-if="loadingBalance">
            <template #template>
              <el-skeleton-item variant="p" />
            </template>
          </el-skeleton>
          <div v-else-if="form.currencyIn">
            {{
              t("c-offers-editform-currentBalance", {
                balance: currentBalanceCoin,
                currencyIn: getSympolById(form.currencyIn),
              })
            }}
          </div>
        </template>
      </el-form-item>
      <el-form-item label=" ">
        <el-button
          round
          size="small"
          class="text-decoration-dotted"
          text
          @click="helpOfferAmountVisible = true"
        >
          {{ t("c-offers-editform-offerAmountExplanation") }}
          <el-icon class="ml-1"><QuestionFilled /></el-icon>
        </el-button>
        <el-dialog
          v-model="helpOfferAmountVisible"
          :title="t('c-offers-editform-offerAmountExplanationTitle')"
          width="500px"
          :fullscreen="fullScreen"
        >
          <div class="font-size-3.5">
            <p>
              {{ t("c-offers-editform-offerAmountExplanationText1") }}
            </p>
            <p>
              {{ t("c-offers-editform-offerAmountExplanationText2") }}
            </p>
            <p>
              {{ t("c-offers-editform-offerAmountExplanationText3") }}
            </p>
            <p>
              {{ t("c-offers-editform-offerAmountExplanationText4") }}
            </p>
          </div>
        </el-dialog>
      </el-form-item>
      <el-divider>{{ t("c-offers-editform-termsOfUse") }}</el-divider>
      <el-alert :closable="false">
        {{ t("c-offers-editform-termsOfUseText") }}
      </el-alert>
      <div class="text-left mt-2">
        <TipTap
          v-model="form.termsOfUse"
          :placeholder="t('c-offers-editform-termsOfUsePlaceholder')"
        ></TipTap>
      </div>
    </el-form>

    <template #footer>
      <div class="text-left mb-2" v-if="textError">
        <el-alert :title="textError" type="error" :closable="false" show-icon />
      </div>
      <div class="dialog-footer">
        <el-button @click="emit('close')" :disabled="loading" round>{{
          t("c-offers-editform-cancel")
        }}</el-button>
        <el-button type="primary" @click="save" :loading="loading" round>
          {{ t("c-offers-editform-confirm") }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.order-type-sell {
  .el-radio-button__original-radio:checked + .el-radio-button__inner {
    background: var(--el-color-danger);
    border-color: var(--el-border-color);
  }
}
.order-type-buy {
  .el-radio-button__original-radio:checked + .el-radio-button__inner {
    background: var(--el-color-success);
    border-color: var(--el-border-color);
  }
}
.offer-form {
  .el-loading-mask {
    background: radial-gradient(
      closest-side,
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 0.5) 60%,
      rgba(0, 0, 0, 0) 99%
    );
  }
}
.el-select-dropdown__header {
  padding: 0 0.5rem;
}
</style>
